/**
 * ScoreDetailsDrawer
 *
 * Right-anchored MUI Drawer that hosts the full Data Quality Score breakdown
 * (Gauge, Metric Tiles, Mismatch Breakdown, History) on-demand.
 *
 * The inner Summary components (Validation / Comparison / GRQ) own their
 * data fetching, so they are only mounted when the drawer is opened AND
 * an executionId is available. This keeps dashboard mount lightweight.
 */
import React from "react";
import { Box, Drawer, IconButton, Typography, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ValidationScoreSummary from "./ValidationScoreSummary";
import ComparisonScoreSummary from "./ComparisonScoreSummary";
import GRQScoreSummary from "./GRQScoreSummary";
import { IComparisonScore } from "../../types/Scoring.type";

export type ScoreDetailsType = "validation" | "comparison" | "generic_rq";

interface IScoreDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  type: ScoreDetailsType;
  executionId: number | null | undefined;
  comparisonScoreData?: IComparisonScore | null;
}

const TITLE_BY_TYPE: Record<ScoreDetailsType, string> = {
  validation: "Validation Score Details",
  comparison: "Comparison Score Details",
  generic_rq: "Research Query Score Details",
};

const ScoreDetailsDrawer: React.FC<IScoreDetailsDrawerProps> = ({
  open,
  onClose,
  type,
  executionId,
  comparisonScoreData,
}) => {
  const shouldRenderContent = open && !!executionId && executionId > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            top: "64px",
          },
        },
      }}
      PaperProps={{
        sx: {
          top: "64px",
          height: "calc(100% - 64px)",
          width: { xs: "100%", sm: "80vw" },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.75,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
            noWrap
          >
            {TITLE_BY_TYPE[type]}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25 }}
          >
            Breakdown, history, and recalculation for this execution.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close score details"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {shouldRenderContent ? (
          <>
            {type === "validation" && (
              <ValidationScoreSummary executionId={executionId} />
            )}
            {type === "comparison" && (
              <ComparisonScoreSummary
                executionId={executionId}
                initialScoreData={comparisonScoreData}
              />
            )}
            {type === "generic_rq" && (
              <GRQScoreSummary executionId={executionId} />
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No execution selected.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ScoreDetailsDrawer;
